/* eslint jsx-a11y/no-static-element-interactions: 0 */
/* eslint class-methods-use-this: 0 */
import throttle from 'lodash/throttle';
import anime from 'animejs';
import logger from 'common/logger';
import formatSettings from './formatSettings';
import WebChat from './webchat';
import WidgetDesign, { setBaseZIndex } from './widget-design';
import SpeakingAvatar from './SpeakingAvatar';
import {
  WIDGET_HIDE,
  WIDGET_CLOSE,
  SET_WIDGET_VIEW,
  SET_WIDGET_OPEN_STATE,
  SEND_MESSAGE_IF_NOT_SENT,
  SET_TEXT_TO_SPEECH,
  BOT_IS_SPEAKING,
  BOT_IS_NOT_SPEAKING,
} from 'common/widgetConstants';

import { Button, Div, Span, Img, setElementStyle, MultilinesText } from './utils';
import {
  BEBOT_WIDGET_LOADING_CONTAINER,
  BEBOT_WIDGET_LOADING_HEAD,
  BEBOT_WIDGET_LOADING_CIRCLE,
  BEBOT_WIDGET,
  BEBOT_WIDGET_ACTIVE,
  BEBOT_ROLLOUT_CONTAINER,
  BEBOT_ROLLOUT_CLOSE_BUTTON,
  BEBOT_FIRST_MESSAGE_BADGE,
  BEBOT_BUBBLE_WRAPPER,
  BEBOT_BUBBLE,
} from './widgetClassNames';
import { closeAnimation, openAnimation, adjustAnimationAfterResize } from './widgetAnimations';
import {
  isMobileView,
  getResponsiveValue,
  setMediaQueryThresholds,
  getSuggestedScale,
} from './responsive';
import Rollout, {
  rolloutAnimation,
  closeRolloutAnimation,
  getRolloutWidth,
  adjustRolloutAfterResize,
} from './rollout';

import bebotHeadPath from './images/bebot-head.svg';
import chatBubblePath from './images/chat-bubble.svg';
import trackEventProvider from 'common/trackEventProvider';

let trackEvent;

const {
  textBox,
  defaultStyle,
  chatStyle,
  chatFullscreenStyle,
  dialogueWrapper,
  dialogueTop,
  dialogueBottom,
  dialogueIcon,
  dialogueCustomIcon,
  dialogueIconImage,
  dialogueCustomIconImage,
  dialogueTextWrapper,
  dialogueTextMainText,
  dialogueTextSubText,
  dialogueCloseButton,
  dialogueBottomText,
  chatWidgetWrapper,
  chatWidget,
  chatWidgetImage,
  chatWidgetText,
  chatLoadingAnimationContainerStyle,
  chatLoadingImageStyle,
  chatLoadingCircleContainerStyle,
  chatLoadingCircleStyle,
  chatNotificationBadge,
} = WidgetDesign;

const getWidgetCard = () => [document.querySelector(`.${BEBOT_WIDGET}`)];

/*
Here's the settings format:
{
  card: {
    title: 'title in the card',
    subTitle: 'subTitle in the card',
    imageUrl: 'url of the icon in the card',
    buttonText: 'text of the button in the card',
    buttonTextColor: 'text's color of the button in the card',
  },

  bubbleButton: {
    imageUrl: 'url of the icon in the bubble',
    backgroundColor: 'Background color of the icon under the card',
    text: 'text under the speech bubble',
    bottomMargin: {
      mobile: 10, // in px
      desktop: 10, // in px
    },
    sideMargin: {
      mobile: 20, // in px
      desktop: 10, // in px
    },
  },

  rollout: {
    text: 'Text of the rollout',
    showOnMobile: false,
  },

  header: {
    logoUrl: 'image logo in webchat header',
    buttonColor: 'color of the buttons in the header (close, reset,  ...)',
    logoText: 'text content under the image logo.',
  },

  bot: {
    avatarUrl: 'the url of the bot avatar',
  },

  userMessage: {
    color: 'text color (default: white)',
    backgroundColor: 'Color of user messages background (default: blue)'
  },

  menu: {
    color: 'text color (default white)'
    backgroundColor: 'Color of top menu (default blue)',
    itemBackgroundColor: 'Color of menu item (default darken the blue',
  },
  wordSuggestions: {
    color: 'text color (default white)',
    backgroundColor: 'default transparent blue',
  },

  screenReaderLabels: {
    regionTitle: 'Chatbot',
    loading: 'Loading',
    dismiss: 'Dismiss',
  },

  zIndex: 'must be an int',

  align: 'one of: left, right (default)',

  mediaQueryThresholds: {
    // browser width threshold deciding if we're in mobile (below the threshold)
    // or desktop (above the threshold) mode
    mobile: 500,
  },

  onOpen: function(){
    // gets called after the widget is opened. can be used to track Bebot usage
  },

  textToSpeech: {
    enabled: false,
    avatar: {
      silent: {
        src: 'http://example.com/silent.png'
      },
      speaking: {
        src: 'http://example.com/speaking.png'
      },
      label: 'Audio'
    }
  }
}
*/
class Widget {
  constructor({ base, key, locale, entrypointToken, settings = {}, featured }) {
    const {
      card = {},
      bubbleButton = {},
      align = 'right',
      onOpen,
      screenReaderLabels,
      rollout,
    } = settings;
    const { title, subTitle, buttonText, buttonTextColor } = card;
    const { backgroundColor, text: bubbleText } = bubbleButton;
    const bottomMargin = { mobile: 10, desktop: 10, ...bubbleButton.bottomMargin };
    const sideMargin = { mobile: 10, desktop: 10, ...bubbleButton.sideMargin };
    let scale = getSuggestedScale();
    // Currently scaling is not ideal - we remove some UI elements and open the chat in a new tab,
    // so let's not bother with scaling when there is less than a 5% difference.
    if (scale > 0.95 && scale < 1.05) scale = 1;
    this.state = {
      base,
      key,
      locale,
      entrypointToken,
      title,
      subTitle,
      bubbleText,
      bottomMargin,
      sideMargin,
      buttonText,
      cardImageUrl: card.imageUrl,
      bubbleImageUrl: bubbleButton.imageUrl,
      backgroundColor,
      buttonTextColor,
      align,
      scale,
      openInNewTab: scale !== 1, // open in a new tab because we can't easily scale the chat
      defaultRolloutText: rollout.text,
      showRolloutOnMobile: rollout.showOnMobile,
      rolloutFeatured: featured,
      settings,
      chatHasOpened: false, // = chat has been opened at least once
      widgetIsOpen: false, // = chat is currently open
      isCardHidden: scale !== 1, // this is only for desktop - on mobile it's always hidden
      onOpen,
      screenReaderLabels,
      textToSpeechIsShown: false,
      textToSpeechIsAllowed: true, // whether user has turned on/off the feature,
      iframeContentWindow: null, // is set only once the iframe has sent a message
    };

    this.bindMethods = this.bindMethods.bind(this);
    this.bindMethods();
    // We need to throttle scroll event handler, so that we don't kill performance
    // or responsiveness of the host page
    this.render();
  }

  onMount() {
    window.addEventListener('resize', this.onResize);
    this.initRolloutIfNeeded();
  }

  onUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  bindMethods() {
    this.openChat = this.openChat.bind(this);
    this.hideChat = this.hideChat.bind(this);
    this.closeChat = this.closeChat.bind(this);
    this.onChatLoad = this.onChatLoad.bind(this);
    this.onIframeLoad = this.onIframeLoad.bind(this);
    this.dismissCard = this.dismissCard.bind(this);
    this.onResize = this.onResize.bind(this);
    this.createHideClass = this.createHideClass.bind(this);
    this.onScroll = throttle(this.onScroll.bind(this), 300);
    this.removeRollout = this.removeRollout.bind(this);
    this.onClickRollout = this.onClickRollout.bind(this);
    this.onDismissRollout = this.onDismissRollout.bind(this);
    this.setChatStyle = this.setChatStyle.bind(this);
    this.prepareChat = this.prepareChat.bind(this);
    this.renderWidgetLoadingAnimation = this.renderWidgetLoadingAnimation.bind(this);
    this.showTextToSpeechIfNeeded = this.showTextToSpeechIfNeeded.bind(this);
    this.hideTextToSpeechIfNeeded = this.hideTextToSpeechIfNeeded.bind(this);
    this.renderTextToSpeechAvatarIfNeeded = this.renderTextToSpeechAvatarIfNeeded.bind(this);
    this.activateTextToSpeech = this.activateTextToSpeech.bind(this);
    this.deactivateTextToSpeech = this.deactivateTextToSpeech.bind(this);
    this.isTextToSpeechDisplayable = this.isTextToSpeechDisplayable.bind(this);
  }

  onResize() {
    this.initRolloutIfNeeded();
    if (!this.state.showRolloutOnMobile) {
      const invisibleOnMobileElements = document.querySelectorAll(
        `.${BEBOT_ROLLOUT_CONTAINER}, .${BEBOT_FIRST_MESSAGE_BADGE}`
      );
      [].forEach.call(invisibleOnMobileElements, element => {
        element.style.visibility = isMobileView() ? 'hidden' : 'visible';
      });
    }
    // Adjust rollout width when the browser window is narrow
    if (this.rolloutContainer && this.state.showRolloutOnMobile) {
      const rolloutWidth = getRolloutWidth(this.state.rolloutText);
      this.state.rolloutWidth = rolloutWidth;
      adjustRolloutAfterResize({ align: this.state.align, width: this.state.rolloutWidth });
    }

    if (isMobileView() && this.chatView && this.chatView.style.display !== 'none') {
      document.body.classList.add(BEBOT_WIDGET_ACTIVE);
    } else {
      document.body.classList.remove(BEBOT_WIDGET_ACTIVE);
    }
    this.updateResponsiveStyles();
    this.setChatStyle();
    this.postMessage({ type: SET_WIDGET_VIEW, isFullScreen: isMobileView() });
    if (this.state.widgetIsOpen) {
      adjustAnimationAfterResize({
        isCardHidden: this.state.isCardHidden,
        chatHasOpened: this.state.chatHasOpened,
        responsiveStyles: this.getResponsiveStyles(),
        align: this.state.align,
      });
      if (this.isTextToSpeechDisplayable()) {
        this.showTextToSpeechIfNeeded();
      } else {
        this.hideTextToSpeechIfNeeded();
      }
    }
  }

  /**
   * Webchat panel render, listeners, etc.
   * */

  setChatStyle() {
    if (!this.chatView || this.chatView.style.display === 'none') return;
    const style = isMobileView() ? chatFullscreenStyle : chatStyle(this.state.align);
    setElementStyle(this.chatView, style);
    this.showTextToSpeechIfNeeded();
  }

  createHideClass() {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      .${BEBOT_WIDGET_ACTIVE} {
        overflow: hidden;
        -webkit-overflow-scrolling: touch;
        height: 100vh;
      }
      .${BEBOT_WIDGET_ACTIVE} > *:not([opacity="1"]) {
        display: none;
      }
    `;
    document.body.appendChild(style);
  }

  animate() {
    openAnimation({
      chatHasOpened: this.state.chatHasOpened,
      isCardHidden: this.state.isCardHidden,
      renderWidgetLoadingAnimation: this.renderWidgetLoadingAnimation,
      prepareChat: this.prepareChat,
      createHideClass: this.createHideClass,
      setChatStyle: this.setChatStyle,
      onLoad: this.onChatLoad,
      align: this.state.align,
    });
  }

  onIframeLoad(iframeContentWindow) {
    this.state.iframeContentWindow = iframeContentWindow;
    window.onmessage = message => {
      if (message.origin !== this.state.base) return;

      switch (message.data) {
        case WIDGET_HIDE:
          this.hideChat();
          break;
        case WIDGET_CLOSE:
          this.closeChat();
          break;
        case BOT_IS_SPEAKING:
          this.speakingAvatar?.setSpeaking(true);
          break;
        case BOT_IS_NOT_SPEAKING:
          this.speakingAvatar?.setSpeaking(false);
          break;
        default:
          logger.warn('Unknown message', message.data);
      }
    };
    this.showTextToSpeechIfNeeded();
  }

  onChatLoad() {
    this.state.chatHasOpened = true;
    this.state.chatLoaded = true;
    if (this.state.rolloutClicked && this.state.rolloutPayload) {
      this.postMessage({
        type: SEND_MESSAGE_IF_NOT_SENT,
        message: { text: this.state.rolloutText, payload: this.state.rolloutPayload },
      });
    }
  }

  activateTextToSpeech() {
    if (!this.state.textToSpeechIsAllowed) return;

    this.postMessage({
      type: SET_TEXT_TO_SPEECH,
      active: true,
    });
  }

  deactivateTextToSpeech() {
    this.postMessage({
      type: SET_TEXT_TO_SPEECH,
      active: false,
    });
  }

  isTextToSpeechDisplayable() {
    return this.state.settings.textToSpeech.enabled && !isMobileView();
  }

  hideTextToSpeechIfNeeded() {
    if (!this.state.textToSpeechIsShown) return;

    this.deactivateTextToSpeech();
    this.speakingAvatar?.hide();
    this.state.textToSpeechIsShown = false;
  }

  renderTextToSpeechAvatarIfNeeded() {
    if (!this.state.settings.textToSpeech?.avatar) return;
    if (this.speakingAvatar) return;

    this.speakingAvatar = SpeakingAvatar({
      align: this.state.align,
      avatar: this.state.settings.textToSpeech.avatar,
      allowed: this.state.textToSpeechIsAllowed,
      control: this.state.settings.textToSpeech.control,
      onSpeechControlChange: ({ allowed }) => {
        this.state.textToSpeechIsAllowed = allowed;
        if (allowed) {
          this.activateTextToSpeech();
        } else {
          this.deactivateTextToSpeech();
        }
      },
    });

    if (this.speakingAvatar?.view) this.view.appendChild(this.speakingAvatar.view);
  }

  showTextToSpeechIfNeeded() {
    if (!this.isTextToSpeechDisplayable()) return;
    if (this.state.textToSpeechIsShown) return;

    this.activateTextToSpeech();
    this.renderTextToSpeechAvatarIfNeeded();
    this.speakingAvatar?.show();
    this.state.textToSpeechIsShown = true;
  }

  setWidgetIsOpen(widgetIsOpen) {
    this.state.widgetIsOpen = widgetIsOpen;
    this.postMessage({ type: SET_WIDGET_OPEN_STATE, widgetIsOpen });
  }

  openChat() {
    if (!this.state.openInNewTab) {
      this.setWidgetIsOpen(true);
      this.removeRollout();
      this.animate();
    }

    if (this.state.onOpen) this.state.onOpen();
    trackEvent({ category: 'Widget', action: 'Open', label: this.state.entrypointToken });

    if (this.state.openInNewTab) {
      // We don't bother with passing all the color customization since the chat will be outside
      // the website anyway.
      const url = `${this.state.base}/chat/${this.state.entrypointToken}?locale=${this.state.locale}`;
      window.open(url, '_blank');
    }
  }

  prepareChat(cb) {
    this.renderChat(cb);
    this.view.appendChild(this.chatView);
    this.setChatStyle();
    this.startSendingScrollEventsToFrame();
  }

  hideChat() {
    this.setWidgetIsOpen(false);
    this.chatView.style.setProperty('display', 'none');
    this.widgetView.style.setProperty('display', 'block');
    this.stopSendingScrollEventsToFrame();
    closeAnimation({
      isCardHidden: this.state.isCardHidden,
      responsiveStyles: this.getResponsiveStyles(),
    });
    this.hideTextToSpeechIfNeeded();
    if (this.state.chatHasOpened) {
      // remove loading indicator and circles from dom after 1st load
      const elem = document.querySelector(`.${BEBOT_WIDGET_LOADING_HEAD}`);
      if (elem) elem.parentNode.removeChild(elem);
      const circles = document.querySelectorAll(`.${BEBOT_WIDGET_LOADING_CIRCLE}`);
      [].forEach.call(circles, e => e.remove());
    }
  }

  closeChat() {
    this.hideChat();
    this.state.chatHasOpened = false;
  }

  startSendingScrollEventsToFrame() {
    window.addEventListener('scroll', this.onScroll);
  }

  stopSendingScrollEventsToFrame() {
    window.removeEventListener('scroll', this.onScroll);
  }

  // This method is bound and throttled in constructor
  onScroll() {
    this.postMessage({ type: 'scroll' });
  }

  postMessage(message) {
    this.state.iframeContentWindow?.postMessage(message, this.state.base);
  }

  renderNotificationBadge() {
    return Div(
      {
        class: BEBOT_FIRST_MESSAGE_BADGE,
        style: chatNotificationBadge,
      },
      document.createTextNode('1')
    );
  }

  initRolloutIfNeeded() {
    if (this.rolloutContainer) return;

    const { scale, showRolloutOnMobile } = this.state;
    if (scale !== 1 || (isMobileView() && !showRolloutOnMobile)) return;

    this.insertRollout();
  }

  setupRollout() {
    const items = this.state.rolloutFeatured;
    if (!items.length) {
      this.state.rolloutText = this.state.defaultRolloutText;
    } else {
      const featured = items[Math.floor(Math.random() * items.length)];
      this.state.rolloutText = featured.title;
      this.state.rolloutPayload = featured.payload;
    }
    this.state.rolloutWidth = getRolloutWidth(this.state.rolloutText);
  }

  insertRollout() {
    this.setupRollout();
    const container = document.querySelector(`.${BEBOT_BUBBLE_WRAPPER}`);
    container.appendChild(this.renderNotificationBadge());
    this.rolloutContainer = Rollout({
      backgroundColor: this.state.backgroundColor,
      align: this.state.align,
      messageText: this.state.rolloutText,
      dismissText: this.state.screenReaderLabels.dismiss,
      onClick: this.onClickRollout,
      onDismiss: this.onDismissRollout,
    });
    container.insertBefore(this.rolloutContainer, container.firstChild);
    rolloutAnimation({
      align: this.state.align,
      width: this.state.rolloutWidth,
    });
  }

  onClickRollout() {
    this.state.rolloutClicked = true;
    this.openChat();
  }

  onDismissRollout() {
    closeRolloutAnimation({ align: this.state.align, removeRollout: this.removeRollout });
  }

  removeRollout() {
    const rolloutContainer = document.querySelector(`.${BEBOT_ROLLOUT_CONTAINER}`);
    if (rolloutContainer) {
      anime.remove(rolloutContainer);
      document
        .querySelector(`.${BEBOT_ROLLOUT_CLOSE_BUTTON}`)
        .removeEventListener('click', this.onDismissRollout);

      rolloutContainer.remove();
    }
    const msgBadge = document.querySelector(`.${BEBOT_FIRST_MESSAGE_BADGE}`);
    if (msgBadge) msgBadge.remove();
  }

  renderWidgetLoadingAnimation() {
    return Div(
      {
        class: BEBOT_WIDGET_LOADING_CONTAINER,
        style: chatLoadingAnimationContainerStyle,
        'aria-busy': 'true',
        'aria-live': 'polite', // like role="alert", but doesn't interrupt the user
      },
      [
        Img({
          class: BEBOT_WIDGET_LOADING_HEAD,
          alt: this.state.screenReaderLabels.loading,
          src: bebotHeadPath,
          style: chatLoadingImageStyle,
        }),
        Div(
          {
            style: chatLoadingCircleContainerStyle,
          },
          [
            Div({
              class: BEBOT_WIDGET_LOADING_CIRCLE,
              style: chatLoadingCircleStyle,
            }),
            Div({
              class: BEBOT_WIDGET_LOADING_CIRCLE,
              style: chatLoadingCircleStyle,
            }),
            Div({
              class: BEBOT_WIDGET_LOADING_CIRCLE,
              style: chatLoadingCircleStyle,
            }),
          ]
        ),
      ]
    );
  }

  renderChat(cb) {
    this.chatView = Div(
      { class: BEBOT_WIDGET, style: chatStyle(this.state.align) },
      WebChat({
        webchatBaseUrl: this.state.base,
        data: {
          entrypointToken: this.state.entrypointToken,
          key: this.state.key,
          locale: this.state.locale,
        },
        startTextToSpeech: this.isTextToSpeechDisplayable(),
        settings: this.state.settings,
        afterLoadAnimation: cb,
        onIframeLoad: this.onIframeLoad,
      })
    );
  }

  /**
   * Dialogue panel render, listeners, etc.
   * */

  dismissCard() {
    anime({
      targets: getWidgetCard(),
      opacity: 0,
      translateY: this.state.chatHasOpened ? 110 : 20,
      duration: 200,
      easing: 'linear',
      complete: () => {
        this.state.isCardHidden = true;
        this.cardView.style.setProperty('display', 'none');
      },
    });
  }

  getBubbleBottomMargin() {
    return getResponsiveValue(this.state.bottomMargin);
  }

  getBubbleSideMargin() {
    return getResponsiveValue(this.state.sideMargin);
  }

  // <bebot logo> Bebot / Type your question  (x) / Chat Now
  // also known as dialogue or text box
  renderCard() {
    const isVisible = !isMobileView() && !this.state.isCardHidden;
    const style = textBox({
      align: this.state.align,
      bottomMargin: this.getBubbleBottomMargin(),
      sideMargin: this.getBubbleSideMargin(),
      isVisible,
    });

    this.cardView = Div(
      { class: BEBOT_WIDGET, style },
      Div(
        { style: defaultStyle },
        Div({ style: dialogueWrapper }, [
          Div({ style: dialogueTop }, [
            Div(
              { style: this.state.cardImageUrl ? dialogueCustomIcon : dialogueIcon },
              Img({
                alt: '', // decorative image - hide from screen readers
                src: this.state.cardImageUrl ? this.state.cardImageUrl : bebotHeadPath,
                style: this.state.cardImageUrl ? dialogueCustomIconImage : dialogueIconImage,
              })
            ),
            Div({ style: dialogueTextWrapper }, [
              Div(
                { style: defaultStyle },
                Span(
                  {
                    class: 'message_title',
                    style: dialogueTextMainText,
                    // prevent google translate to JP 'Bebot' > 'ベボット'
                    translate: 'no',
                  },
                  document.createTextNode(this.state.title)
                )
              ),
              Div(
                { style: defaultStyle },
                Span(
                  {
                    class: 'message_content',
                    style: dialogueTextSubText,
                  },
                  document.createTextNode(this.state.subTitle)
                )
              ),
            ]),
          ]),
          Button(
            {
              type: 'button',
              style: dialogueBottom,
              events: {
                click: this.openChat,
              },
            },
            Span(
              {
                style: dialogueBottomText(
                  !this.state.buttonTextColor ? undefined : this.state.buttonTextColor
                ),
              },
              document.createTextNode(this.state.buttonText)
            )
          ),
          Button(
            {
              type: 'button',
              events: {
                click: this.dismissCard,
              },
              style: dialogueCloseButton,
              // prevent google translate to JP 'x' > 'NS'
              translate: 'no',
              'aria-label': this.state.screenReaderLabels.dismiss,
            },
            document.createTextNode('×')
          ),
        ])
      )
    );

    return this.cardView;
  }

  renderButton() {
    const { backgroundColor, align } = this.state;
    this.bubbleImage = Img({
      alt: '', // decorative image - hide from screen readers
      src: this.state.bubbleImageUrl ? this.state.bubbleImageUrl : chatBubblePath,
      style: chatWidgetImage(isMobileView(), this.state.bubbleText, this.state.bubbleImageUrl),
    });
    const children = [this.bubbleImage];
    if (this.state.bubbleText !== '') {
      children.push(Span({ style: chatWidgetText }, MultilinesText(this.state.bubbleText)));
    }
    this.bubbleButton = Button(
      {
        type: 'button',
        style: chatWidget(backgroundColor, isMobileView()),
        events: {
          click: this.openChat,
        },
        class: BEBOT_BUBBLE,
      },
      children
    );
    this.bubbleWrapper = Div(
      {
        style: chatWidgetWrapper({
          align,
          bottomMargin: this.getBubbleBottomMargin(),
          sideMargin: this.getBubbleSideMargin(),
          scale: this.state.scale,
        }),
        class: BEBOT_BUBBLE_WRAPPER,
      },
      this.bubbleButton
    );
    return this.bubbleWrapper;
  }

  getResponsiveStyles() {
    const isMobile = isMobileView();
    const { align, isCardHidden, backgroundColor, bubbleText, bubbleImageUrl, scale } = this.state;
    // on mobile card wrapper is hidden
    const isCardVisible = !isCardHidden && !isMobile;
    // om mobile bottom margin may be different
    const bottomMargin = this.getBubbleBottomMargin();
    const sideMargin = this.getBubbleSideMargin();
    return {
      bubbleWrapper: chatWidgetWrapper({ align, bottomMargin, sideMargin, scale }),
      cardView: textBox({ align, bottomMargin, sideMargin, isVisible: isCardVisible }),
      bubbleButton: chatWidget(backgroundColor, isMobile),
      bubbleImage: chatWidgetImage(isMobile, bubbleText, bubbleImageUrl),
    };
  }

  updateResponsiveStyles() {
    const styles = this.getResponsiveStyles();
    setElementStyle(this.bubbleWrapper, styles.bubbleWrapper);
    setElementStyle(this.cardView, styles.cardView);
    setElementStyle(this.bubbleImage, styles.bubbleImage);
    setElementStyle(this.bubbleButton, styles.bubbleButton);
  }

  /**
   * main render
   * */
  render() {
    const style = Object.assign({}, defaultStyle, { left: this.state.align ? '10px' : 'unset' });
    this.view = Div({
      style: defaultStyle,
      lang: this.state.locale,
      // The aria 'region' role makes it easier for screen reader users to find the widget
      // without having to listen to the entire website content beforehand
      role: 'region',
      'aria-label': this.state.screenReaderLabels.regionTitle,
    });
    this.widgetView = Div({ style }, [this.renderCard(), this.renderButton()]);
    this.view.appendChild(this.widgetView);
    return this.view;
  }
}

const renderWidget = ({
  elem,
  base,
  key,
  locale,
  settings: customSettings,
  defaultSettings,
  entrypointToken,
  featured,
  clientSessionId,
}) => {
  const settings = formatSettings(customSettings, defaultSettings);
  trackEvent = trackEventProvider({
    baseUrl: base,
    windowObject: window,
    clientSessionId,
    widgetKey: key,
  });
  const { mediaQueryThresholds, zIndex } = settings;
  setMediaQueryThresholds(mediaQueryThresholds);
  setBaseZIndex(zIndex);

  const widget = new Widget({ base, key, locale, entrypointToken, settings, featured });
  elem.appendChild(widget.view);
  widget.onMount();
  trackEvent({
    category: 'Widget',
    action: 'View',
    label: entrypointToken,
    nonInteraction: true,
  });

  const widgetApi = {
    remove: () => {
      widget.onUnmount();
      elem.removeChild(widget.view);
    },
    open: () => {
      widget.openChat();
    },
    close: () => {
      widget.closeChat();
    },
  };

  return widgetApi;
};

export default renderWidget;
